import React, { useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

import { Theme, useTheme, styled } from "@mui/material/styles";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";

import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";

import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";

import MenuIcon from "@mui/icons-material/Menu";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import LoginIcon from "@mui/icons-material/Login";
import AssessmentIcon from "@mui/icons-material/Assessment";
import DescriptionIcon from "@mui/icons-material/Description";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import { useCompanyRole } from "../../servicos/useCompanyRole";
import BugReportIcon from "@mui/icons-material/BugReport";
import HelpIcon from "@mui/icons-material/Help";
import GroupIcon from "@mui/icons-material/Group";

// Management Icons
import CommuteIcon from "@mui/icons-material/Commute";
import CardTravelIcon from "@mui/icons-material/CardTravel";
import PowerIcon from "@mui/icons-material/Power";
import SettingsPowerIcon from "@mui/icons-material/SettingsPower";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import LeakAddIcon from "@mui/icons-material/LeakAdd";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import EmojiTransportationIcon from "@mui/icons-material/EmojiTransportation";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import FireplaceIcon from "@mui/icons-material/Fireplace";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";

import FactCheckIcon from "@mui/icons-material/FactCheck";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import DomainIcon from "@mui/icons-material/Domain";
import CreditCardIcon from "@mui/icons-material/CreditCard";

import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import BackupIcon from "@mui/icons-material/Backup";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import DvrIcon from "@mui/icons-material/Dvr";
import AddToQueueIcon from "@mui/icons-material/AddToQueue";
import MultilineChartIcon from "@mui/icons-material/MultilineChart";

import FileDownloadIcon from "@mui/icons-material/FileDownload";

import AppContexto from "../../servicos/app-contexto/AppContexto";
import { useSubscription } from "../../contexts/SubscriptionContext";
import { ReactContext } from "../../App";
import Usuario from "../../servicos/usuario/Usuario";

import UserTitle from "../../componentes/template/widgets/user/UserTitle";
import { useTour } from "../../components/tour/TourProvider";

import AppRouterConfig from "../../AppRouterConfig";
import { Avatar, Collapse } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { designTokens } from "../../theme/designTokens";

type Anchor = "top" | "left" | "bottom" | "right";

// Styled components to replace makeStyles
const StyledListItemText = styled(ListItemText)({
	"& span": {
		fontSize: "1.0rem",
	},
});

const StyledListItemIcon = styled(ListItemIcon)({
	minWidth: 36,
	maxWidth: 56,
	"& .MuiSvgIcon-root": {
		fontSize: "1.2rem",
	},
});

const _StyledAccordionButton = styled(ListItem)({
	width: "100%",
	display: "flex",
	justifyContent: "space-between",
	"&:hover": {
		backgroundColor: designTokens.colors.hover,
		"& .MuiListSubheader-root": {
			color: "#000",
			backgroundColor: "transparent !important",
		},
		"& .MuiSvgIcon-root": {
			color: "#000",
		},
	},
	"& .MuiList-subheader": {
		paddingTop: "4px",
		paddingBottom: "4px",
	},
	"& .MuiListSubheader-root": {
		color: "#000",
		fontSize: "1rem",
		lineHeight: "1.5rem",
		backgroundColor: "transparent",
	},
	"& .MuiListSubheader-gutters": {
		paddingLeft: "0px",
	},
	"& .MuiSvgIcon-root": {
		fontSize: "1.2rem",
	},
});

const StyledCollapseSubmenu = styled(List)({
	"& .MuiListItem-root": {
		paddingLeft: "32px",
	},
	"& .MuiTypography-body1": {
		lineHeight: "1.2rem",
	},
});

const MenuButtonContainer = styled("div")({
	display: "flex",
	justifyContent: "flex-end",
});

const MenuButton = styled(Button)({
	padding: "12px 5px",
	"&:hover": {
		color: "#ffffff",
		backgroundColor: designTokens.colors.hover,
		"& > span svg": {
			color: "#fff",
		},
	},
});

const StyledMenuIcon = styled(MenuIcon)({
	"&:hover": {
		color: "#fff",
	},
});

const DrawerList = styled("div")({
	width: 300,
	"& .MuiDivider-root": {
		marginLeft: "16px",
		marginRight: "16px",
	},
	// Section titles (Company, Scope 1-3, ...) render as plain bold text on
	// the row — never as the theme's white sticky box. The doubled class
	// selector outranks the global MuiListSubheader-sticky override.
	"& .MuiListSubheader-root, & .MuiListSubheader-root.MuiListSubheader-sticky":
		{
			position: "static",
			backgroundColor: "transparent",
			color: "#000",
			fontSize: "1rem",
			fontWeight: 700,
			lineHeight: 1.5,
			paddingLeft: 0,
		},
	// Section rows match the height of every other menu row
	"& .MuiList-padding": {
		paddingTop: "8px",
		paddingBottom: "8px",
	},
	"& .MuiListSubheader-root + .MuiSvgIcon-root": {
		marginLeft: "8px",
	},
	// Hovered rows highlight in the brand colour with white text
	"& .MuiListItem-button:hover": {
		backgroundColor: designTokens.colors.hover,
		"& .MuiListItemText-primary": {
			color: "#fff",
		},
		"& .MuiListSubheader-root, & .MuiListSubheader-root.MuiListSubheader-sticky":
			{
				color: "#fff",
				backgroundColor: "transparent",
			},
	},
});

const DrawerFullList = styled("div")({
	width: "auto",
});

const UserInfoContainer = styled("div")({
	display: "flex",
	padding: "10px 10px 0 10px",
	flexWrap: "wrap",
	"& > *": {
		margin: "0 auto",
		width: "100%",
	},
});

const UserAccountButton = styled(Button)({
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	"& > span": {
		flexDirection: "column",
		alignItems: "center",
	},
});

export default function SwipeableTemporaryDrawer() {
	///
	/// AppContexto
	///
	const appContexto: AppContexto = React.useContext(ReactContext);
	const _theme: Theme = useTheme();
	const { logout, loginWithRedirect, isAuthenticated } = useAuth0();
	const { startTour } = useTour();
	const { hasFeature } = useSubscription();
	const activeRole = useCompanyRole();

	///
	/// States
	///
	const [user, setUser] = React.useState<Usuario | null>(null);
	const [_openCollapse, setOpen] = React.useState(false);
	const [accordionItens, setAccordionItens] = React.useState<string[]>([]);

	const [state, setState] = React.useState({
		top: false,
		left: false,
		bottom: false,
		right: false,
	});

	///
	/// useEffects
	///
	React.useEffect(() => {
		appContexto.usuario
			.VerificaLogado()
			.then((_userInfo) => {
				setUser(appContexto.usuario);
			})
			.catch((error) => {
				// Expected when user is not authenticated
				if (error.message !== "User not authenticated") {
					console.error("Menu: Authentication error:", error);
				}
				setUser(null);
			});
	}, [appContexto.usuario]);

	///
	/// eventos
	///
	const toggleDrawer = useCallback(
		(anchor: Anchor, open: boolean) =>
			(event: React.KeyboardEvent | React.MouseEvent) => {
				if (
					event &&
					event.type === "keydown" &&
					((event as React.KeyboardEvent).key === "Tab" ||
						(event as React.KeyboardEvent).key === "Shift")
				) {
					return;
				}
				if (
					event &&
					event.type === "keydown" &&
					(event as React.KeyboardEvent).key === "Escape"
				) {
					setState({ ...state, [anchor]: open });
				}
				if (event.type === "keydown") {
					return;
				}
				setOpen(false);
				setState({ ...state, [anchor]: open });
			},
		// TODO(lint): exhaustive-deps disabled — needs careful review of dep array
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	// const fnChangeDark = (sender: any) =>
	// {
	//     appContexto.storage_isDark_save( !appContexto.getIsDark );
	// };

	///
	/// Menu
	///
	const _navigate = useNavigate();

	const handleClickAccordion = (itemList: string) => {
		const pos = accordionItens.indexOf(itemList);
		if (pos === -1) {
			setAccordionItens((atual) => [...atual, itemList]);
		} else {
			accordionItens.splice(pos, 1);
			setAccordionItens((_atual) => [...accordionItens]);
		}
	};

	const list = (anchor: Anchor) => {
		const ListContainer =
			anchor === "top" || anchor === "bottom"
				? DrawerFullList
				: DrawerList;

		return (
			<ListContainer
				role="presentation"
				onKeyDown={toggleDrawer(anchor, false)}
			>
				{/* {user?  */}
				<UserInfoContainer>
					<Paper elevation={0}>
						<Box
							display="flex"
							width="100%"
							height="100%"
							alignItems="center"
							justifyContent="center"
							flexDirection="column"
						>
							<Box>
								<Link
									to={AppRouterConfig.USER.ACCOUNT}
									onClick={toggleDrawer(anchor, false)}
								>
									<UserAccountButton>
										{user?.Info?.picture ? (
											<Avatar
												alt=""
												src={user?.Info?.picture}
												style={{
													width: "80px",
													height: "80px",
												}}
											/>
										) : (
											<AccountCircleIcon
												color="primary"
												style={{
													fontSize: "5.1875rem",
												}}
											/>
										)}
										<UserTitle
											user={user?.Info || undefined}
										/>
									</UserAccountButton>
								</Link>
							</Box>
						</Box>
					</Paper>
				</UserInfoContainer>
				{/* : null} */}
				{!isAuthenticated ? (
					<>
						<ListItem
							button
							key="Login"
							onClick={() => loginWithRedirect()}
						>
							<StyledListItemIcon>
								<LoginIcon />
							</StyledListItemIcon>
							<StyledListItemText primary="Log In" />
						</ListItem>
					</>
				) : (
					<>
						<ListItem
							button
							key="Logout"
							onClick={() => {
								// Clear tour session data on logout
								sessionStorage.removeItem("arteh_tour_started");
								logout({
									logoutParams: {
										returnTo: window.location.origin,
									},
								});
							}}
						>
							<StyledListItemIcon>
								<ExitToAppIcon />
							</StyledListItemIcon>
							<StyledListItemText primary="Logout" />
						</ListItem>
					</>
				)}
				<Divider />
				{user ? (
					<>
						<Link
							to={AppRouterConfig.USERCOMPANY.DASHBOARD.INDEX}
							onClick={toggleDrawer(anchor, false)}
							className="dashboard-link"
						>
							<ListItem button key="Dashboard">
								<StyledListItemIcon>
									<AssessmentIcon />
								</StyledListItemIcon>
								<StyledListItemText primary="Dashboard" />
							</ListItem>
						</Link>
						<Link
							to={AppRouterConfig.USERCOMPANY.REPORT.INDEX}
							onClick={toggleDrawer(anchor, false)}
							className="reports-link"
						>
							<ListItem button key="Reports">
								<StyledListItemIcon>
									<DescriptionIcon />
								</StyledListItemIcon>
								<StyledListItemText primary="Reports" />
							</ListItem>
						</Link>
						{hasFeature("s2Report") && (
							<Link
								to={AppRouterConfig.USERCOMPANY.REPORT.S2REPORT}
								onClick={toggleDrawer(anchor, false)}
							>
								<ListItem button key="AASB S2 report">
									<StyledListItemIcon>
										<DescriptionIcon />
									</StyledListItemIcon>
									<StyledListItemText primary="AASB S2 report" />
								</ListItem>
							</Link>
						)}
						{hasFeature("dataQualityScore") && (
							<Link
								to={
									AppRouterConfig.USERCOMPANY.REPORT
										.NGER_REPORT
								}
								onClick={toggleDrawer(anchor, false)}
							>
								<ListItem button key="NGER report">
									<StyledListItemIcon>
										<DescriptionIcon />
									</StyledListItemIcon>
									<StyledListItemText primary="NGER report" />
								</ListItem>
							</Link>
						)}
						<Link
							to={AppRouterConfig.USERCOMPANY.REPORT.PORTFOLIO}
							onClick={toggleDrawer(anchor, false)}
						>
							<ListItem button key="Portfolio">
								<StyledListItemIcon>
									<AssessmentIcon />
								</StyledListItemIcon>
								<StyledListItemText primary="Portfolio" />
							</ListItem>
						</Link>
						{hasFeature("s2Report") && (
							<Link
								to={
									AppRouterConfig.USERCOMPANY.REPORT
										.CLIMATE_RISKS
								}
								onClick={toggleDrawer(anchor, false)}
							>
								<ListItem button key="Risk register">
									<StyledListItemIcon>
										<AssessmentIcon />
									</StyledListItemIcon>
									<StyledListItemText primary="Risk register" />
								</ListItem>
							</Link>
						)}
						{hasFeature("s2Report") && (
							<Link
								to={
									AppRouterConfig.USERCOMPANY.REPORT
										.CLIMATE_TARGETS
								}
								onClick={toggleDrawer(anchor, false)}
							>
								<ListItem button key="Climate targets">
									<StyledListItemIcon>
										<AssessmentIcon />
									</StyledListItemIcon>
									<StyledListItemText primary="Climate targets" />
								</ListItem>
							</Link>
						)}
						{hasFeature("s2Report") && (
							<Link
								to={
									AppRouterConfig.USERCOMPANY.REPORT
										.DISCLOSURE_WORKSPACE
								}
								onClick={toggleDrawer(anchor, false)}
							>
								<ListItem button key="Disclosure workspace">
									<StyledListItemIcon>
										<HistoryEduIcon />
									</StyledListItemIcon>
									<StyledListItemText primary="Disclosure workspace" />
								</ListItem>
							</Link>
						)}
						{hasFeature("supplierRequests") && (
							<Link
								to={
									AppRouterConfig.USERCOMPANY.REPORT
										.SUPPLIER_REQUESTS
								}
								onClick={toggleDrawer(anchor, false)}
							>
								<ListItem button key="Supplier requests">
									<StyledListItemIcon>
										<GroupIcon />
									</StyledListItemIcon>
									<StyledListItemText primary="Supplier requests" />
								</ListItem>
							</Link>
						)}
						{hasFeature("multiEntityConsolidation") && (
							<Link
								to={AppRouterConfig.USERCOMPANY.REPORT.GROUPS}
								onClick={toggleDrawer(anchor, false)}
							>
								<ListItem button key="Group consolidation">
									<StyledListItemIcon>
										<DomainIcon />
									</StyledListItemIcon>
									<StyledListItemText primary="Group consolidation" />
								</ListItem>
							</Link>
						)}
						{hasFeature("assuranceRegisters") && (
							<Link
								to={
									AppRouterConfig.USERCOMPANY.REPORT.ASSURANCE
								}
								onClick={toggleDrawer(anchor, false)}
							>
								<ListItem button key="Assurance">
									<StyledListItemIcon>
										<VpnKeyIcon />
									</StyledListItemIcon>
									<StyledListItemText primary="Assurance" />
								</ListItem>
							</Link>
						)}
						{hasFeature("dataQualityScore") && (
							<Link
								to={
									AppRouterConfig.USERCOMPANY.REPORT.DATA_QUALITY
								}
								onClick={toggleDrawer(anchor, false)}
							>
								<ListItem button key="DataQuality">
									<StyledListItemIcon>
										<FactCheckIcon />
									</StyledListItemIcon>
									<StyledListItemText primary="Data quality" />
								</ListItem>
							</Link>
						)}
						{hasFeature("baselining") && (
							<Link
								to={
									AppRouterConfig.USERCOMPANY.REPORT.BASELINES
								}
								onClick={toggleDrawer(anchor, false)}
							>
								<ListItem button key="Baselines">
									<StyledListItemIcon>
										<VpnKeyIcon />
									</StyledListItemIcon>
									<StyledListItemText primary="Baselines" />
								</ListItem>
							</Link>
						)}
						{hasFeature("dataPackExport") && (
							<Link
								to={
									AppRouterConfig.USERCOMPANY.REPORT.DATA_PACK
								}
								onClick={toggleDrawer(anchor, false)}
							>
								<ListItem button key="Customer data pack">
									<StyledListItemIcon>
										<FileDownloadIcon />
									</StyledListItemIcon>
									<StyledListItemText primary="Customer data pack" />
								</ListItem>
							</Link>
						)}
						{/* FILES */}
						<Link
							to={AppRouterConfig.USERCOMPANY.DATA.FILEUPLOAD}
							onClick={toggleDrawer(anchor, false)}
							className="upload-data-link"
						>
							<ListItem button key="File upload">
								<StyledListItemIcon>
									<BackupIcon />
								</StyledListItemIcon>
								<StyledListItemText primary="File upload" />
							</ListItem>
						</Link>
						<Link
							to={AppRouterConfig.USERCOMPANY.DATA.FILELOG}
							onClick={toggleDrawer(anchor, false)}
						>
							<ListItem button key="All files">
								<StyledListItemIcon>
									<DescriptionIcon />
								</StyledListItemIcon>
								<StyledListItemText primary="All files" />
							</ListItem>
						</Link>
						<Divider />
						<Link
							to="/members"
							onClick={toggleDrawer(anchor, false)}
						>
							<ListItem button key="Team Members">
								<StyledListItemIcon>
									<GroupIcon />
								</StyledListItemIcon>
								<StyledListItemText primary="Team Members" />
							</ListItem>
						</Link>
						<Link
							to={AppRouterConfig.USER.REPORT_BUG}
							onClick={toggleDrawer(anchor, false)}
						>
							<ListItem button key="Report Bug">
								<StyledListItemIcon>
									<BugReportIcon />
								</StyledListItemIcon>
								<StyledListItemText primary="Report Bug" />
							</ListItem>
						</Link>
						<ListItem
							button
							key="Restart Tour"
							onClick={(event) => {
								startTour();
								toggleDrawer(anchor, false)(event);
							}}
						>
							<StyledListItemIcon>
								<HelpIcon />
							</StyledListItemIcon>
							<StyledListItemText primary="Restart Tour" />
						</ListItem>
						<Divider />
						{activeRole !== "assurer" && (
							<>
								{/* COMPANY */}
								<List>
							<ListItem
								button
								onClick={() => {
									handleClickAccordion("company");
								}}
							>
								<ListSubheader>Company</ListSubheader>
								{accordionItens.indexOf("company") >= 0 ? (
									<ExpandLess />
								) : (
									<ExpandMore />
								)}
							</ListItem>
							<Collapse
								in={
									accordionItens.indexOf("company") >= 0
										? true
										: false
								}
								timeout="auto"
								unmountOnExit
							>
								<StyledCollapseSubmenu disablePadding>
									<Link
										to={
											AppRouterConfig.MANAGEMENT
												.FACILITIES
										}
										onClick={toggleDrawer(anchor, false)}
									>
										<ListItem
											button
											key="Facility"
											className="menu-facilities"
										>
											<StyledListItemIcon>
												<DomainIcon />
											</StyledListItemIcon>
											<StyledListItemText primary="Facilities" />
										</ListItem>
									</Link>
									{/* Commented out Profit menu item */}
									{/* <Link
									to={AppRouterConfig.MANAGEMENT.PROFIT}
									onClick={toggleDrawer(anchor, false)}
								>
									<ListItem button key="Profit">
										<StyledListItemIcon>
											<AttachMoneyIcon />
										</StyledListItemIcon>
										<StyledListItemText
											primary="Profit"
										/>
									</ListItem>
								</Link> */}
									<Link
										to={AppRouterConfig.MANAGEMENT.VEHICLE}
										onClick={toggleDrawer(anchor, false)}
									>
										<ListItem button key="Vehicle">
											<StyledListItemIcon>
												<DirectionsCarIcon />
											</StyledListItemIcon>
											<StyledListItemText primary="Vehicles" />
										</ListItem>
									</Link>
									{hasFeature("customEmissionFactors") && (
										<Link
											to={
												AppRouterConfig.MANAGEMENT
													.CUSTOMFACTORS
											}
											onClick={toggleDrawer(
												anchor,
												false,
											)}
										>
											<ListItem
												button
												key="Custom factors"
											>
												<StyledListItemIcon>
													<MultilineChartIcon />
												</StyledListItemIcon>
												<StyledListItemText primary="Custom emission factors" />
											</ListItem>
										</Link>
									)}
								</StyledCollapseSubmenu>
							</Collapse>
						</List>
						<Divider />
						{/* SCOPE 1 */}
						<List>
							<ListItem
								button
								className="menu-scope1-accordion"
								onClick={() => {
									handleClickAccordion("scope1");
								}}
							>
								<ListSubheader>Scope 1</ListSubheader>
								{accordionItens.indexOf("scope1") >= 0 ? (
									<ExpandLess />
								) : (
									<ExpandMore />
								)}
							</ListItem>
							<Collapse
								in={
									accordionItens.indexOf("scope1") >= 0
										? true
										: false
								}
								timeout="auto"
								unmountOnExit
							>
								<StyledCollapseSubmenu disablePadding>
									<Link
										to={
											AppRouterConfig.MANAGEMENT.EQUIPMENT
										}
										onClick={toggleDrawer(anchor, false)}
									>
										<ListItem button key="Equipment">
											<StyledListItemIcon>
												<SettingsPowerIcon />
											</StyledListItemIcon>
											<StyledListItemText primary="Equipment" />
										</ListItem>
									</Link>
									<Link
										to={
											AppRouterConfig.MANAGEMENT
												.FUELCOMBUSTED
										}
										onClick={toggleDrawer(anchor, false)}
										className="menu-fuels-combusted-link"
									>
										<ListItem button key="Fuelcombusted">
											<StyledListItemIcon>
												<LocalGasStationIcon />
											</StyledListItemIcon>
											<StyledListItemText primary="Fuels Combusted" />
										</ListItem>
									</Link>
									<Link
										to={
											AppRouterConfig.MANAGEMENT
												.FUGITIVEGASES
										}
										onClick={toggleDrawer(anchor, false)}
									>
										<ListItem button key="Fugitivegases">
											<StyledListItemIcon>
												<LeakAddIcon />
											</StyledListItemIcon>
											<StyledListItemText primary="Fugitive from Gas Leakage" />
										</ListItem>
									</Link>
									<Link
										to={
											AppRouterConfig.MANAGEMENT
												.FUGITIVEPROCESS
										}
										onClick={toggleDrawer(anchor, false)}
									>
										<ListItem button key="Fugitiveprocess">
											<StyledListItemIcon>
												<LeakAddIcon />
											</StyledListItemIcon>
											<StyledListItemText primary="Fugitive from Processes" />
										</ListItem>
									</Link>
									<Link
										to={
											AppRouterConfig.MANAGEMENT
												.PROCESSESCALCINATION
										}
										onClick={toggleDrawer(anchor, false)}
									>
										<ListItem
											button
											key="Processescalcination"
										>
											<StyledListItemIcon>
												<FireplaceIcon />
											</StyledListItemIcon>
											<StyledListItemText primary="Processes (Calcination)" />
										</ListItem>
									</Link>
									<Link
										to={
											AppRouterConfig.MANAGEMENT
												.VEHICLEUSAGE
										}
										onClick={toggleDrawer(anchor, false)}
									>
										<ListItem button key="Vehicle Usage">
											<StyledListItemIcon>
												<LocalShippingIcon />
											</StyledListItemIcon>
											<StyledListItemText primary="Vehicle Usage" />
										</ListItem>
									</Link>
									<Link
										to={
											AppRouterConfig.MANAGEMENT
												.OTHEREMISSIONS + "/1"
										}
										onClick={toggleDrawer(anchor, false)}
									>
										<ListItem button key="Otheremissions">
											<StyledListItemIcon>
												<EmojiTransportationIcon />
											</StyledListItemIcon>
											<StyledListItemText primary="Other Emissions" />
										</ListItem>
									</Link>
								</StyledCollapseSubmenu>
							</Collapse>
						</List>
						<Divider />
						{/* SCOPE 2 */}
						<List>
							<ListItem
								button
								className="menu-scope2-accordion"
								onClick={() => {
									handleClickAccordion("scope2");
								}}
							>
								<ListSubheader>Scope 2</ListSubheader>
								{accordionItens.indexOf("scope2") >= 0 ? (
									<ExpandLess />
								) : (
									<ExpandMore />
								)}
							</ListItem>
							<Collapse
								in={
									accordionItens.indexOf("scope2") >= 0
										? true
										: false
								}
								timeout="auto"
								unmountOnExit
							>
								<StyledCollapseSubmenu disablePadding>
									<Link
										to={
											AppRouterConfig.MANAGEMENT
												.ELECTRICITY
										}
										onClick={toggleDrawer(anchor, false)}
										className="menu-electricity-link"
									>
										<ListItem button key="Electricity">
											<StyledListItemIcon>
												<PowerIcon />
											</StyledListItemIcon>
											<StyledListItemText primary="Electricity" />
										</ListItem>
									</Link>
									<Link
										to={
											AppRouterConfig.USERCOMPANY.DATA
												.BILLUPLOAD
										}
										onClick={toggleDrawer(anchor, false)}
									>
										<ListItem button key="BillUpload">
											<StyledListItemIcon>
												<ReceiptLongIcon />
											</StyledListItemIcon>
											<StyledListItemText primary="Import from Bill" />
										</ListItem>
									</Link>
									<Link
										to={
											AppRouterConfig.MANAGEMENT
												.OTHEREMISSIONS + "/2"
										}
										onClick={toggleDrawer(anchor, false)}
									>
										<ListItem button key="Otheremissions">
											<StyledListItemIcon>
												<EmojiTransportationIcon />
											</StyledListItemIcon>
											<StyledListItemText primary="Other Emissions" />
										</ListItem>
									</Link>
								</StyledCollapseSubmenu>
							</Collapse>
						</List>
						<Divider />
						{/* SCOPE 3 */}
						<List>
							<ListItem
								button
								onClick={() => {
									handleClickAccordion("scope3");
								}}
							>
								<ListSubheader>Scope 3</ListSubheader>
								{accordionItens.indexOf("scope3") >= 0 ? (
									<ExpandLess />
								) : (
									<ExpandMore />
								)}
							</ListItem>
							<Collapse
								in={
									accordionItens.indexOf("scope3") >= 0
										? true
										: false
								}
								timeout="auto"
								unmountOnExit
							>
								<StyledCollapseSubmenu disablePadding>
									{hasFeature("scope3Categories") && (
										<Link
											to={
												AppRouterConfig.MANAGEMENT
													.SCOPE3CATEGORIES
											}
											onClick={toggleDrawer(
												anchor,
												false,
											)}
										>
											<ListItem
												button
												key="Scope 3 categories"
											>
												<StyledListItemIcon>
													<AssessmentIcon />
												</StyledListItemIcon>
												<StyledListItemText primary="Scope 3 categories" />
											</ListItem>
										</Link>
									)}
									{hasFeature("spendBasedScope3") && (
										<Link
											to={
												AppRouterConfig.MANAGEMENT
													.SCOPE3ENTRIES
											}
											onClick={toggleDrawer(
												anchor,
												false,
											)}
										>
											<ListItem
												button
												key="Scope 3 entries"
											>
												<StyledListItemIcon>
													<AssessmentIcon />
												</StyledListItemIcon>
												<StyledListItemText primary="Scope 3 entries" />
											</ListItem>
										</Link>
									)}
									{hasFeature("spendBasedScope3") && (
										<Link
											to={
												AppRouterConfig.MANAGEMENT
													.SPENDBASED
											}
											onClick={toggleDrawer(
												anchor,
												false,
											)}
										>
											<ListItem
												button
												key="Spend-based Scope 3"
											>
												<StyledListItemIcon>
													<CreditCardIcon />
												</StyledListItemIcon>
												<StyledListItemText primary="Spend-based Scope 3" />
											</ListItem>
										</Link>
									)}
									<Link
										to={
											AppRouterConfig.MANAGEMENT
												.BUSINESSTRAVELS
										}
										onClick={toggleDrawer(anchor, false)}
									>
										<ListItem button key="Businesstravels">
											<StyledListItemIcon>
												<CardTravelIcon />
											</StyledListItemIcon>
											<StyledListItemText primary="Business Travels" />
										</ListItem>
									</Link>
									<Link
										to={
											AppRouterConfig.MANAGEMENT.COMMUTING
										}
										onClick={toggleDrawer(anchor, false)}
									>
										<ListItem button key="Commuting">
											<StyledListItemIcon>
												<CommuteIcon />
											</StyledListItemIcon>
											<StyledListItemText primary="Commuting" />
										</ListItem>
									</Link>
									<Link
										to={
											AppRouterConfig.MANAGEMENT
												.TRANSPORTROAD
										}
										onClick={toggleDrawer(anchor, false)}
									>
										<ListItem button key="Transportroad">
											<StyledListItemIcon>
												<DirectionsBusIcon />
											</StyledListItemIcon>
											<StyledListItemText primary="Transport & Distribution (Road)" />
										</ListItem>
									</Link>
									<Link
										to={
											AppRouterConfig.MANAGEMENT
												.TRANSPORTSEA
										}
										onClick={toggleDrawer(anchor, false)}
									>
										<ListItem button key="Transportsea">
											<StyledListItemIcon>
												<DirectionsBoatIcon />
											</StyledListItemIcon>
											<StyledListItemText primary="Transport & Distribution (Sea)" />
										</ListItem>
									</Link>
									<Link
										to={
											AppRouterConfig.MANAGEMENT
												.OTHEREMISSIONS + "/3"
										}
										onClick={toggleDrawer(anchor, false)}
									>
										<ListItem button key="Otheremissions">
											<StyledListItemIcon>
												<EmojiTransportationIcon />
											</StyledListItemIcon>
											<StyledListItemText primary="Other Emissions" />
										</ListItem>
									</Link>
								</StyledCollapseSubmenu>
							</Collapse>
						</List>
						<Divider />
						{/* MACC */}
						<List>
							<ListItem
								button
								onClick={() => {
									handleClickAccordion("macc");
								}}
							>
								<ListSubheader>
									Decarbonisation Simulator
								</ListSubheader>
								{accordionItens.indexOf("macc") >= 0 ? (
									<ExpandLess />
								) : (
									<ExpandMore />
								)}
							</ListItem>
							<Collapse
								in={
									accordionItens.indexOf("macc") >= 0
										? true
										: false
								}
								timeout="auto"
								unmountOnExit
							>
								<StyledCollapseSubmenu disablePadding>
									<Link
										to={
											AppRouterConfig.MACC
												.ARTEHDECARBONIZATIONSTRATEGIES
										}
										onClick={toggleDrawer(anchor, false)}
									>
										<ListItem
											button
											key="ArtehDecarbonizationStrategies"
										>
											<StyledListItemIcon>
												<DvrIcon />
											</StyledListItemIcon>
											<StyledListItemText primary="Built-in Decarbonisation Strategies" />
										</ListItem>
									</Link>
									<Link
										to={
											AppRouterConfig.MACC
												.COMPANYDECARBONIZATIONSTRATEGIES
										}
										onClick={toggleDrawer(anchor, false)}
									>
										<ListItem
											button
											key="CompanyDecarbonizationStrategies"
										>
											<StyledListItemIcon>
												<AddToQueueIcon />
											</StyledListItemIcon>
											<StyledListItemText primary="Company Decarbonisation Strategies" />
										</ListItem>
									</Link>
									<Link
										to={
											AppRouterConfig.MACC
												.SCENARIOSIMULATION
										}
										onClick={toggleDrawer(anchor, false)}
									>
										<ListItem
											button
											key="ScenarioSimulation"
										>
											<StyledListItemIcon>
												<MultilineChartIcon />
											</StyledListItemIcon>
											<StyledListItemText primary="Abatement Investment Model" />
										</ListItem>
									</Link>
								</StyledCollapseSubmenu>
							</Collapse>
								</List>
							</>
						)}
						<Divider />
						{/* ACCOUNT */}
						<List>
							<ListItem
								button
								onClick={() => {
									handleClickAccordion("account");
								}}
							>
								<ListSubheader>Account</ListSubheader>
								{accordionItens.indexOf("account") >= 0 ? (
									<ExpandLess />
								) : (
									<ExpandMore />
								)}
							</ListItem>
							<Collapse
								in={
									accordionItens.indexOf("account") >= 0
										? true
										: false
								}
								timeout="auto"
								unmountOnExit
							>
								<StyledCollapseSubmenu disablePadding>
									<Link
										to={AppRouterConfig.USER.ACCOUNT}
										onClick={toggleDrawer(anchor, false)}
									>
										<ListItem button key="My account">
											<StyledListItemIcon>
												<AccountCircleIcon />
											</StyledListItemIcon>
											<StyledListItemText primary="My account" />
										</ListItem>
									</Link>
									<Link
										to={
											AppRouterConfig.USER.CUSTOMER_PORTAL
										}
										onClick={toggleDrawer(anchor, false)}
									>
										<ListItem
											button
											key="Billing & Subscription"
										>
											<StyledListItemIcon>
												<CreditCardIcon />
											</StyledListItemIcon>
											<StyledListItemText primary="Billing & Subscription" />
										</ListItem>
									</Link>
									<Link
										to={AppRouterConfig.USER.CHANGEPASSWORD}
										onClick={toggleDrawer(anchor, false)}
									>
										<ListItem button key="Change password">
											<StyledListItemIcon>
												<VpnKeyIcon />
											</StyledListItemIcon>
											<StyledListItemText primary="Change password" />
										</ListItem>
									</Link>
								</StyledCollapseSubmenu>
							</Collapse>
						</List>
					</>
				) : (
					<></>
				)}
			</ListContainer>
		);
	};

	///
	/// Render
	///

	return (
		<MenuButtonContainer>
			{
				// ['left', 'right', 'top', 'bottom'] as Anchor[]).map
				((anchor = "right" as Anchor) => (
					<React.Fragment key={anchor}>
						<MenuButton
							className="menu-button"
							onClick={toggleDrawer(anchor, true)}
						>
							<StyledMenuIcon htmlColor="#7cced1" />
						</MenuButton>
						<SwipeableDrawer
							disableSwipeToOpen={false}
							anchor={anchor}
							open={state[anchor]}
							onClose={toggleDrawer(anchor, false)}
							onOpen={toggleDrawer(anchor, true)}
							style={{ zIndex: 1300 }}
							ModalProps={{
								keepMounted: false,
								BackdropProps: {
									style: {
										backgroundColor: "rgba(0, 0, 0, 0.5)",
									},
								},
							}}
						>
							{list(anchor)}
						</SwipeableDrawer>
					</React.Fragment>
				))()
			}
		</MenuButtonContainer>
	);
}
